/**
 * ChatPage — E2E Encrypted Team DM Chat
 * - ECDH P-256 key exchange + AES-256-GCM per-message encryption
 * - Grey ✓✓ = delivered to device  |  Blue ✓✓ = read by recipient
 * - @mention autocomplete + space-chime notification
 * Spacemancraft Pvt Ltd
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { getApp } from 'firebase/app';
import {
  getFirestore, collection, addDoc, onSnapshot, doc, getDoc, updateDoc,
  query, orderBy, serverTimestamp, limit, writeBatch, setDoc,
} from 'firebase/firestore';
import { createNotification, extractMentions, playSpaceChime } from '../notifUtils';
import { useTheme } from '../appContext';

const db = () => getFirestore(getApp());

// ─────────────────────────────────────────────────────────────────────────────
//  THEME — mutable module-level object, updated inside ChatPage on each render
//  so all sub-components automatically pick up the selected app theme.
// ─────────────────────────────────────────────────────────────────────────────
let T = {
  bg:     '#090c14',
  panel:  '#111827',
  panel2: '#0e1420',
  border: '#1e2d45',
  text:   '#c8d4e8',
  bright: '#eef2ff',
  dim:    '#4a5a78',
  muted:  '#6b7fa0',
  accent: '#6366f1',
  green:  '#10b981',
  blue:   '#38bdf8',
  red:    '#f43f5e',
  yellow: '#f59e0b',
  mono:   "'JetBrains Mono', monospace",
  sans:   "'Inter', system-ui, sans-serif",
};

const ROLE_COLOR = {
  admin:'#ef4444', mp:'#6366f1', embedded:'#f59e0b',
  system:'#10b981', ops:'#e11d48', mentor:'#0ea5e9', guest:'#94a3b8',
};

export function dmChatId(uid1, uid2) {
  return [uid1, uid2].sort().join('_');
}

// ─────────────────────────────────────────────────────────────────────────────
//  E2E ENCRYPTION  (ECDH P-256 + AES-256-GCM)
// ─────────────────────────────────────────────────────────────────────────────
const PRIV_KEY_LS = uid => `smc_ecdh_priv_${uid}`;

async function generateKeyPair() {
  return crypto.subtle.generateKey(
    { name: 'ECDH', namedCurve: 'P-256' },
    true,
    ['deriveKey', 'deriveBits']
  );
}

async function exportJwk(key) {
  return crypto.subtle.exportKey('jwk', key);
}

async function importPrivJwk(jwk) {
  return crypto.subtle.importKey(
    'jwk', jwk, { name: 'ECDH', namedCurve: 'P-256' }, false, ['deriveKey', 'deriveBits']
  );
}

async function importPubJwk(jwk) {
  return crypto.subtle.importKey(
    'jwk', jwk, { name: 'ECDH', namedCurve: 'P-256' }, false, []
  );
}

async function initMyKeys(uid) {
  // Returns { privateKey, pubJwk, isNew }
  const stored = localStorage.getItem(PRIV_KEY_LS(uid));
  if (stored) {
    try {
      const privJwk = JSON.parse(stored);
      const privateKey = await importPrivJwk(privJwk);
      return { privateKey, pubJwk: null, isNew: false };
    } catch (_) { /* corrupt, regenerate */ }
  }
  const kp = await generateKeyPair();
  const privJwk = await exportJwk(kp.privateKey);
  const pubJwk  = await exportJwk(kp.publicKey);
  localStorage.setItem(PRIV_KEY_LS(uid), JSON.stringify(privJwk));
  return { privateKey: kp.privateKey, pubJwk, isNew: true };
}

async function deriveSharedKey(myPrivKey, theirPubJwk) {
  const theirPub = await importPubJwk(theirPubJwk);
  return crypto.subtle.deriveKey(
    { name: 'ECDH', public: theirPub },
    myPrivKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

async function encryptMsg(plaintext, aesKey) {
  const iv      = crypto.getRandomValues(new Uint8Array(12));
  const encoded = new TextEncoder().encode(plaintext);
  const ct      = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, aesKey, encoded);
  const toB64   = buf => btoa(String.fromCharCode(...new Uint8Array(buf)));
  return { iv: toB64(iv.buffer), ct: toB64(ct) };
}

async function decryptMsg(ivB64, ctB64, aesKey) {
  try {
    const fromB64 = b => Uint8Array.from(atob(b), c => c.charCodeAt(0));
    const dec = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: fromB64(ivB64) }, aesKey, fromB64(ctB64)
    );
    return new TextDecoder().decode(dec);
  } catch (_) {
    return '[encrypted — open on original device]';
  }
}

// ─────────────────────────────────────────────────────────────────────────────
//  UI HELPERS
// ─────────────────────────────────────────────────────────────────────────────
function fmtTime(ts) {
  if (!ts) return '';
  const d   = ts.toDate ? ts.toDate() : new Date(ts);
  const now = new Date();
  return d.toDateString() === now.toDateString()
    ? d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

function Avatar({ name, size = 32, color = T.accent }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: `${color}18`, border: `1px solid ${color}44`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: T.mono, fontSize: size * 0.35, color, fontWeight: 700,
    }}>
      {(name || '?').slice(0, 2).toUpperCase()}
    </div>
  );
}

function MentionText({ text }) {
  if (!text) return null;
  const parts = text.split(/(@\S+)/g);
  return (
    <>
      {parts.map((p, i) =>
        p.startsWith('@')
          ? <span key={i} style={{ color: T.yellow, fontWeight: 600 }}>{p}</span>
          : <span key={i}>{p}</span>
      )}
    </>
  );
}

// Tick indicator: ✓ sent | ✓✓ grey delivered | ✓✓ blue read
function Ticks({ msg }) {
  if (msg.readAt) {
    return <span style={{ color: T.blue, fontSize: 11, letterSpacing: -2 }}>✓✓</span>;
  }
  if (msg.delivered) {
    return <span style={{ color: T.muted, fontSize: 11, letterSpacing: -2 }}>✓✓</span>;
  }
  return <span style={{ color: T.dim, fontSize: 11 }}>✓</span>;
}

function ReportShareCard({ msg, isMine }) {
  const color = msg.portalColor || '#6366f1';
  return (
    <div style={{
      display: 'flex', flexDirection: isMine ? 'row-reverse' : 'row',
      alignItems: 'flex-end', gap: 7, marginBottom: 4,
    }}>
      {!isMine && (
        <Avatar name={msg.senderName} size={28} color={ROLE_COLOR[msg.senderRole] || T.accent} />
      )}
      <div style={{ maxWidth: '80%', minWidth: 200 }}>
        {!isMine && (
          <div style={{ fontFamily: T.mono, fontSize: 8, color: T.muted, marginBottom: 4, paddingLeft: 4 }}>
            {msg.senderName} · shared a report
          </div>
        )}
        {/* Report card */}
        <div style={{
          background: `${color}10`, border: `1px solid ${color}44`,
          borderRadius: isMine ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
          overflow: 'hidden',
        }}>
          {/* Portal label bar */}
          <div style={{
            background: `${color}20`, borderBottom: `1px solid ${color}33`,
            padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />
            <span style={{ fontFamily: T.mono, fontSize: 9, letterSpacing: 1, color }}>
              {(msg.portalLabel || msg.portalId || 'REPORT').toUpperCase()} · SHARED REPORT
            </span>
          </div>
          {/* Title */}
          <div style={{ padding: '10px 14px' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.bright, marginBottom: msg.shareNote ? 6 : 10, lineHeight: 1.35 }}>
              {msg.reportTitle || 'Untitled'}
            </div>
            {msg.shareNote && (
              <div style={{ fontSize: 12, color: T.text, fontStyle: 'italic', marginBottom: 10, lineHeight: 1.4 }}>
                "{msg.shareNote}"
              </div>
            )}
            <a
              href={msg.portalPath || '/'}
              onClick={e => { e.preventDefault(); window.location.href = msg.portalPath; }}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '5px 12px', borderRadius: 6,
                background: `${color}20`, border: `1px solid ${color}55`,
                color, textDecoration: 'none', fontFamily: T.mono, fontSize: 9, letterSpacing: 1,
              }}
            >
              OPEN REPORT →
            </a>
          </div>
        </div>
        <div style={{
          fontFamily: T.mono, fontSize: 8, color: T.dim, marginTop: 3,
          display: 'flex', gap: 5, alignItems: 'center',
          justifyContent: isMine ? 'flex-end' : 'flex-start',
          paddingLeft: 4, paddingRight: 4,
        }}>
          <span>{fmtTime(msg.createdAt)}</span>
          {isMine && <Ticks msg={msg} />}
        </div>
      </div>
    </div>
  );
}

function MessageBubble({ msg, isMine }) {
  // Report share — render as structured card
  if (msg.type === 'report_share') {
    return <ReportShareCard msg={msg} isMine={isMine} />;
  }

  const text = msg.decryptedText ?? msg.text ?? '';
  return (
    <div style={{
      display: 'flex', flexDirection: isMine ? 'row-reverse' : 'row',
      alignItems: 'flex-end', gap: 7, marginBottom: 4,
    }}>
      {!isMine && (
        <Avatar name={msg.senderName} size={28}
          color={ROLE_COLOR[msg.senderRole] || T.accent} />
      )}
      <div style={{ maxWidth: '72%', minWidth: 60 }}>
        {!isMine && (
          <div style={{ fontFamily: T.mono, fontSize: 8, color: T.muted, marginBottom: 2, paddingLeft: 4 }}>
            {msg.senderName}
          </div>
        )}
        <div style={{
          padding: '8px 12px',
          borderRadius: isMine ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
          background: isMine ? `${T.accent}28` : T.panel2,
          border: `1px solid ${isMine ? T.accent + '50' : T.border}`,
          fontSize: 13, color: T.bright, lineHeight: 1.55, wordBreak: 'break-word',
        }}>
          {msg.decryptedText !== undefined
            ? <MentionText text={text} />
            : msg.encrypted
              ? <span style={{ color: T.muted, fontStyle: 'italic', fontSize: 11 }}>🔐 decrypting…</span>
              : <MentionText text={text} />
          }
        </div>
        <div style={{
          fontFamily: T.mono, fontSize: 8, color: T.dim, marginTop: 3,
          display: 'flex', gap: 5, alignItems: 'center',
          justifyContent: isMine ? 'flex-end' : 'flex-start',
          paddingLeft: 4, paddingRight: 4,
        }}>
          {msg.encrypted && <span title="End-to-end encrypted" style={{ color: T.green, fontSize: 9 }}>🔒</span>}
          <span>{fmtTime(msg.createdAt)}</span>
          {isMine && <Ticks msg={msg} />}
        </div>
      </div>
    </div>
  );
}

function MentionSuggestions({ suggestions, onSelect }) {
  if (!suggestions.length) return null;
  return (
    <div style={{
      position: 'absolute', bottom: '100%', left: 0, right: 60, marginBottom: 6,
      background: T.panel, border: `1px solid ${T.border}`, borderRadius: 10,
      overflow: 'hidden', zIndex: 20, boxShadow: '0 8px 32px #00000070',
    }}>
      {suggestions.map(m => {
        const rc = ROLE_COLOR[m.role] || T.accent;
        return (
          <button key={m.uid} type="button" onClick={() => onSelect(m)}
            style={{
              width: '100%', padding: '9px 14px', display: 'flex', alignItems: 'center',
              gap: 10, background: 'transparent', border: 'none', cursor: 'pointer',
              borderBottom: `1px solid ${T.border}33`, textAlign: 'left',
            }}
            onMouseEnter={e => e.currentTarget.style.background = T.panel2}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            <Avatar name={m.name || m.email} size={26} color={rc} />
            <div>
              <div style={{ fontSize: 13, color: T.bright }}>{m.name || m.email}</div>
              <div style={{ fontFamily: T.mono, fontSize: 8, color: rc }}>
                {(m.role || 'member').toUpperCase()}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export default function ChatPage({ user, compact = false }) {
  const myUid  = user?.uid  || '';
  const myName = user?.name || 'Me';
  const myRole = user?.role || 'member';

  // Sync module-level T from app theme on every render so all sub-components
  // pick up the currently selected theme automatically.
  const _theme = useTheme();
  T = {
    bg:     _theme.bg,
    panel:  _theme.panel,
    panel2: _theme.cardBg || _theme.panel,
    border: _theme.border,
    text:   _theme.text,
    bright: _theme.bright,
    dim:    _theme.muted,
    muted:  _theme.muted,
    accent: _theme.accent,
    green:  '#10b981',
    blue:   '#38bdf8',
    red:    '#f43f5e',
    yellow: '#f59e0b',
    mono:   _theme.monoFont,
    sans:   _theme.font,
  };

  // ── State ──────────────────────────────────────────────────────────────────
  const [members,       setMembers]       = useState([]);
  const [activeMember,  setActiveMember]  = useState(null);
  const [rawMessages,   setRawMessages]   = useState([]);
  const [messages,      setMessages]      = useState([]); // decrypted
  const [newMsg,        setNewMsg]        = useState('');
  const [sending,       setSending]       = useState(false);
  const [suggestions,   setSuggestions]   = useState([]);
  const [search,        setSearch]        = useState('');
  const [cryptoReady,   setCryptoReady]   = useState(false);
  const [encryptionErr, setEncryptionErr] = useState(null);
  const [lastSeenMsgId, setLastSeenMsgId] = useState(null);
  const [isMobile,      setIsMobile]      = useState(() => window.innerWidth < 768);

  // ── Refs ───────────────────────────────────────────────────────────────────
  const bottomRef     = useRef(null);
  const inputRef      = useRef(null);
  const msgUnsubRef   = useRef(null);
  const myPrivKeyRef  = useRef(null);
  const sharedKeyRef  = useRef(null);   // AES key for active chat
  const isActiveRef   = useRef(true);   // tab visibility
  const prevMsgCount  = useRef(0);

  // ─────────────────────────────────────────────────────────────────────────
  //  INIT ECDH KEY PAIR
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!myUid) return;
    let cancelled = false;
    initMyKeys(myUid).then(async ({ privateKey, pubJwk, isNew }) => {
      if (cancelled) return;
      myPrivKeyRef.current = privateKey;
      if (isNew && pubJwk) {
        // Upload public key to Firestore so others can encrypt to us
        try {
          await updateDoc(doc(db(), 'authorized_users', myUid), { pubKey: pubJwk });
        } catch (_) {
          try { await setDoc(doc(db(), 'authorized_users', myUid), { pubKey: pubJwk }, { merge: true }); } catch (_2) {}
        }
      }
      setCryptoReady(true);
    }).catch(err => {
      console.error('Crypto init failed:', err);
      setEncryptionErr('Encryption unavailable — messages sent unencrypted');
      setCryptoReady(true);
    });
    return () => { cancelled = true; };
  }, [myUid]);

  // ─────────────────────────────────────────────────────────────────────────
  //  LOAD TEAM MEMBERS
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!myUid) return;
    const unsub = onSnapshot(
      collection(db(), 'authorized_users'),
      snap => {
        setMembers(
          snap.docs
            .map(d => ({ uid: d.id, ...d.data() }))
            .filter(m => m.uid && m.uid !== myUid && m.active !== false)
            .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
        );
      },
      err => console.warn('Members load error:', err)
    );
    return () => unsub();
  }, [myUid]);

  // ─────────────────────────────────────────────────────────────────────────
  //  DERIVE SHARED KEY when active member changes
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    sharedKeyRef.current = null;
    if (!activeMember?.pubKey || !myPrivKeyRef.current) return;
    let cancelled = false;
    deriveSharedKey(myPrivKeyRef.current, activeMember.pubKey).then(key => {
      if (!cancelled) sharedKeyRef.current = key;
    }).catch(err => console.warn('Key derivation failed:', err));
    return () => { cancelled = true; };
  }, [activeMember?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─────────────────────────────────────────────────────────────────────────
  //  SUBSCRIBE TO MESSAGES
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (msgUnsubRef.current) { msgUnsubRef.current(); msgUnsubRef.current = null; }
    setRawMessages([]);
    setMessages([]);
    prevMsgCount.current = 0;
    if (!activeMember?.uid || !myUid) return;

    const chatId = dmChatId(myUid, activeMember.uid);
    const q = query(
      collection(db(), 'chats', chatId, 'messages'),
      orderBy('createdAt', 'asc'),
      limit(200)
    );

    msgUnsubRef.current = onSnapshot(q, snap => {
      const docs = snap.docs.map(d => ({ id: d.id, ref: d.ref, ...d.data() }));
      setRawMessages(docs);

      // Mark incoming messages as DELIVERED immediately
      const toDeliver = docs.filter(
        m => m.senderId === activeMember.uid && !m.delivered
      );
      if (toDeliver.length) {
        const batch = writeBatch(db());
        toDeliver.forEach(m => batch.update(m.ref, { delivered: true }));
        batch.commit().catch(() => {});
      }

      // Mark as READ if chat window is active
      if (isActiveRef.current) {
        const toRead = docs.filter(
          m => m.senderId === activeMember.uid && !m.readAt
        );
        if (toRead.length) {
          const batch = writeBatch(db());
          toRead.forEach(m => batch.update(m.ref, { readAt: serverTimestamp() }));
          batch.commit().catch(() => {});
        }
      }

      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 60);
    }, err => console.warn('Chat subscription error:', err));

    return () => { msgUnsubRef.current?.(); };
  }, [activeMember?.uid, myUid]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─────────────────────────────────────────────────────────────────────────
  //  DECRYPT MESSAGES whenever rawMessages or sharedKey changes
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!rawMessages.length) { setMessages([]); return; }
    let cancelled = false;

    async function decryptAll() {
      const result = [];
      for (const msg of rawMessages) {
        if (msg.encrypted && msg.ct && msg.iv) {
          if (sharedKeyRef.current) {
            const text = await decryptMsg(msg.iv, msg.ct, sharedKeyRef.current);
            result.push({ ...msg, decryptedText: text });
          } else {
            // Key not ready yet — show placeholder, will re-run when key is ready
            result.push({ ...msg, decryptedText: undefined });
          }
        } else {
          // Unencrypted legacy message
          result.push({ ...msg, decryptedText: msg.text || '' });
        }
      }
      if (!cancelled) setMessages(result);
    }

    decryptAll();
    return () => { cancelled = true; };
  }, [rawMessages]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-decrypt when shared key becomes available
  const sharedKeyVersion = useRef(0);
  useEffect(() => {
    if (!sharedKeyRef.current || !rawMessages.length) return;
    sharedKeyVersion.current++;
    const ver = sharedKeyVersion.current;
    let cancelled = false;

    async function redecrypt() {
      const result = [];
      for (const msg of rawMessages) {
        if (msg.encrypted && msg.ct && msg.iv && sharedKeyRef.current) {
          const text = await decryptMsg(msg.iv, msg.ct, sharedKeyRef.current);
          result.push({ ...msg, decryptedText: text });
        } else {
          result.push({ ...msg, decryptedText: msg.text || '' });
        }
      }
      if (!cancelled && ver === sharedKeyVersion.current) setMessages(result);
    }

    // Small delay to let sharedKeyRef settle
    const t = setTimeout(redecrypt, 100);
    return () => { cancelled = true; clearTimeout(t); };
  }, [activeMember?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─────────────────────────────────────────────────────────────────────────
  //  SOUND + NOTIFICATION for new incoming messages
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!messages.length || !activeMember) return;
    const newCount = messages.filter(m => m.senderId === activeMember.uid).length;
    if (newCount > prevMsgCount.current && prevMsgCount.current > 0) {
      // New message from them arrived while we have the chat open
      playSpaceChime();
    }
    prevMsgCount.current = newCount;
  }, [messages.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mark read when window gains focus
  useEffect(() => {
    const onFocus = () => {
      isActiveRef.current = true;
      if (!activeMember?.uid || !myUid) return;
      const chatId = dmChatId(myUid, activeMember.uid);
      // Re-mark any unread as read
      const q2 = query(
        collection(db(), 'chats', chatId, 'messages'),
        orderBy('createdAt', 'asc'), limit(200)
      );
      // Handled in subscription — just set flag
    };
    const onBlur = () => { isActiveRef.current = false; };
    window.addEventListener('focus', onFocus);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('blur', onBlur);
    };
  }, [activeMember?.uid, myUid]);

  // Scroll when messages update
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  // Mobile detection
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  //  @MENTION AUTOCOMPLETE
  // ─────────────────────────────────────────────────────────────────────────
  function handleInput(e) {
    const val = e.target.value;
    setNewMsg(val);
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';

    const cursor = e.target.selectionStart;
    const before = val.slice(0, cursor);
    const atIdx  = before.lastIndexOf('@');
    if (atIdx !== -1) {
      const q2 = before.slice(atIdx + 1);
      if (!q2.includes(' ') && q2.length < 25) {
        const lq = q2.toLowerCase();
        setSuggestions(
          members.filter(m => (m.name || m.email || '').toLowerCase().startsWith(lq)).slice(0, 5)
        );
        return;
      }
    }
    setSuggestions([]);
  }

  function selectMention(member) {
    const cursor = inputRef.current?.selectionStart || newMsg.length;
    const before = newMsg.slice(0, cursor);
    const atIdx  = before.lastIndexOf('@');
    const first  = (member.name || member.email || '').split(' ')[0];
    const next   = newMsg.slice(0, atIdx) + '@' + first + ' ' + newMsg.slice(cursor);
    setNewMsg(next);
    setSuggestions([]);
    setTimeout(() => {
      inputRef.current?.focus();
      const pos = atIdx + first.length + 2;
      inputRef.current?.setSelectionRange(pos, pos);
    }, 0);
  }

  // ─────────────────────────────────────────────────────────────────────────
  //  SEND MESSAGE
  // ─────────────────────────────────────────────────────────────────────────
  const send = useCallback(async (e) => {
    e?.preventDefault();
    const text = newMsg.trim();
    if (!text || !activeMember || sending || !myUid) return;

    setSending(true);
    setNewMsg('');
    if (inputRef.current) inputRef.current.style.height = 'auto';

    const chatId = dmChatId(myUid, activeMember.uid);

    try {
      let msgDoc = {
        senderId:   myUid,
        senderName: myName,
        senderRole: myRole,
        createdAt:  serverTimestamp(),
        delivered:  false,
        readAt:     null,
      };

      // Encrypt if both keys are available
      if (sharedKeyRef.current) {
        const { iv, ct } = await encryptMsg(text, sharedKeyRef.current);
        msgDoc = { ...msgDoc, iv, ct, encrypted: true };
      } else {
        msgDoc.text = text;
        msgDoc.encrypted = false;
      }

      await addDoc(collection(db(), 'chats', chatId, 'messages'), msgDoc);

      // Notify recipient
      await createNotification({
        recipientId: activeMember.uid,
        type:        'message',
        title:       `💬 ${myName}`,
        body:        text.length > 70 ? text.slice(0, 70) + '…' : text,
        fromId:      myUid,
        fromName:    myName,
        link:        'chat',
      });

      // @mention notifications
      const mentioned = extractMentions(text, members).filter(m => m.uid !== activeMember.uid);
      for (const m of mentioned) {
        await createNotification({
          recipientId: m.uid,
          type:        'mention',
          title:       `🚀 ${myName} mentioned you`,
          body:        text.length > 70 ? text.slice(0, 70) + '…' : text,
          fromId:      myUid,
          fromName:    myName,
          link:        'chat',
        });
      }
    } catch (err) {
      console.error('Send failed:', err);
      setNewMsg(text); // restore if failed
    } finally {
      setSending(false);
    }
  }, [newMsg, activeMember, sending, myUid, myName, myRole, members]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (suggestions.length) { selectMention(suggestions[0]); return; }
      send();
    }
    if (e.key === 'Escape') setSuggestions([]);
  }

  function openChat(member) {
    setActiveMember(member);
    setRawMessages([]);
    setMessages([]);
    setSuggestions([]);
    prevMsgCount.current = 0;
    sharedKeyRef.current = null;
    // Derive shared key for this member immediately
    if (member.pubKey && myPrivKeyRef.current) {
      deriveSharedKey(myPrivKeyRef.current, member.pubKey)
        .then(key => { sharedKeyRef.current = key; })
        .catch(() => {});
    }
  }

  const filteredMembers = search.trim()
    ? members.filter(m => (m.name || m.email || '').toLowerCase().includes(search.toLowerCase()))
    : members;

  // ─────────────────────────────────────────────────────────────────────────
  //  RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div style={{
      display: 'flex',
      height: compact ? '100%' : isMobile ? '100dvh' : 'calc(100vh - 110px)',
      minHeight: compact ? 0 : isMobile ? 0 : 500,
      background: T.bg, fontFamily: T.sans,
      borderRadius: (compact || isMobile) ? 0 : 12,
      border: (compact || isMobile) ? 'none' : `1px solid ${T.border}`,
      overflow: 'hidden',
      position: isMobile ? 'fixed' : 'relative',
      top: isMobile ? 0 : 'auto',
      left: isMobile ? 0 : 'auto',
      right: isMobile ? 0 : 'auto',
      bottom: isMobile ? 0 : 'auto',
      zIndex: isMobile ? 100 : 'auto',
    }}>

      {/* ── Sidebar ─────────────────────────────────────────────────────── */}
      <div style={{
        width: isMobile ? '100%' : compact ? 120 : 260,
        flexShrink: 0,
        background: T.panel2, borderRight: isMobile ? 'none' : `1px solid ${T.border}`,
        display: isMobile && activeMember ? 'none' : 'flex',
        flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{
          padding: compact ? '10px 8px 8px' : isMobile ? '16px 16px 12px' : '14px 14px 10px',
          borderBottom: `1px solid ${T.border}`,
        }}>
          <div style={{ fontFamily: T.mono, fontSize: isMobile ? 11 : 9, color: T.accent, letterSpacing: 2, marginBottom: (compact && !isMobile) ? 0 : 8, fontWeight: 700 }}>
            💬 {compact && !isMobile ? 'CHAT' : 'TEAM CHAT'}
          </div>
          {(!compact || isMobile) && (
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search…"
              style={{
                width: '100%', background: T.panel, border: `1px solid ${T.border}`,
                borderRadius: isMobile ? 20 : 8, padding: isMobile ? '10px 16px' : '7px 10px', color: T.text,
                fontFamily: T.sans, fontSize: isMobile ? 15 : 12, outline: 'none', boxSizing: 'border-box',
              }}
            />
          )}
        </div>

        {/* Encryption status */}
        {(!compact || isMobile) && (
          <div style={{
            padding: '5px 12px',
            background: encryptionErr ? '#2a1a0a' : '#0a1f10',
            borderBottom: `1px solid ${T.border}`,
            fontFamily: T.mono, fontSize: 8,
            color: encryptionErr ? T.yellow : T.green,
            display: 'flex', alignItems: 'center', gap: 5,
          }}>
            {encryptionErr ? '⚠ ' + encryptionErr : cryptoReady ? '🔐 E2E ENCRYPTED' : '⏳ INITIALISING…'}
          </div>
        )}

        {/* Member list */}
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {filteredMembers.map(m => {
            const isActive = activeMember?.uid === m.uid;
            const rc = ROLE_COLOR[m.role] || T.accent;
            const hasKey = !!m.pubKey;
            return (
              <button
                key={m.uid}
                type="button"
                onClick={() => openChat(m)}
                style={{
                  width: '100%',
                  padding: isMobile ? '14px 16px' : compact ? '8px 8px' : '11px 14px',
                  display: 'flex', alignItems: 'center',
                  gap: isMobile ? 14 : compact ? 6 : 10,
                  background: isActive ? `${T.accent}14` : 'transparent',
                  border: 'none', cursor: 'pointer', textAlign: 'left',
                  borderBottom: `1px solid ${T.border}${isMobile ? '44' : '22'}`,
                  borderLeft: `3px solid ${isActive ? T.accent : 'transparent'}`,
                  transition: 'background 0.12s',
                }}
                onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = `${T.border}18`; }}
                onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
              >
                <div style={{ position: 'relative', flexShrink: 0 }}>
                  <Avatar name={m.name || m.email} size={isMobile ? 46 : compact ? 28 : 36} color={rc} />
                  <div style={{
                    position: 'absolute', bottom: 0, right: 0,
                    width: isMobile ? 12 : 8, height: isMobile ? 12 : 8, borderRadius: '50%',
                    background: hasKey ? T.green : T.dim,
                    border: `2px solid ${T.panel2}`,
                  }} title={hasKey ? 'Encrypted' : 'No encryption key yet'} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: isMobile ? 16 : compact ? 10 : 13, fontWeight: 600,
                    color: isActive ? T.bright : T.text,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {m.name || m.email || ''}
                  </div>
                  {(!compact || isMobile) && (
                    <div style={{ fontFamily: T.mono, fontSize: isMobile ? 11 : 8, color: rc, marginTop: 2 }}>
                      {(m.role || 'member').toUpperCase()}
                    </div>
                  )}
                </div>
                {isMobile && (
                  <div style={{ color: T.dim, fontSize: 18, flexShrink: 0 }}>›</div>
                )}
              </button>
            );
          })}
          {filteredMembers.length === 0 && (
            <div style={{ padding: '40px 12px', textAlign: 'center', fontFamily: T.mono, fontSize: 9, color: T.dim }}>
              No members
            </div>
          )}
        </div>
      </div>

      {/* ── Chat area ───────────────────────────────────────────────────── */}
      {activeMember ? (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, background: T.bg,
          width: isMobile ? '100%' : undefined }}>

          {/* Chat header */}
          <div style={{
            padding: isMobile ? '10px 14px' : compact ? '8px 12px' : '11px 18px',
            borderBottom: `1px solid ${T.border}`,
            display: 'flex', alignItems: 'center', gap: isMobile ? 12 : 10,
            background: T.panel, flexShrink: 0,
          }}>
            {isMobile && (
              <button
                type="button"
                onClick={() => setActiveMember(null)}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: T.accent, fontSize: 24, padding: '0 4px', lineHeight: 1,
                  display: 'flex', alignItems: 'center', flexShrink: 0,
                }}
              >‹</button>
            )}
            <Avatar
              name={activeMember.name || activeMember.email}
              size={isMobile ? 38 : compact ? 30 : 36}
              color={ROLE_COLOR[activeMember.role] || T.accent}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: isMobile ? 16 : compact ? 12 : 14, fontWeight: 700, color: T.bright,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {activeMember.name || activeMember.email}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: T.green }} />
                <span style={{ fontFamily: T.mono, fontSize: isMobile ? 10 : 8, color: ROLE_COLOR[activeMember.role] || T.muted }}>
                  {(activeMember.role || 'member').toUpperCase()}
                </span>
                {activeMember.pubKey
                  ? <span style={{ fontFamily: T.mono, fontSize: isMobile ? 10 : 8, color: T.green }}>🔐 E2E</span>
                  : <span style={{ fontFamily: T.mono, fontSize: isMobile ? 10 : 8, color: T.yellow }}>⚠ no key</span>
                }
              </div>
            </div>
          </div>

          {/* Messages */}
          <div style={{
            flex: 1, overflowY: 'auto',
            padding: isMobile ? '12px 12px' : compact ? '10px 10px' : '16px 18px',
            display: 'flex', flexDirection: 'column', gap: 2,
          }}>
            {messages.length === 0 && (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', gap: 10, opacity: 0.5 }}>
                <div style={{ fontSize: compact ? 32 : 44 }}>🛸</div>
                <div style={{ fontFamily: T.mono, fontSize: 9, color: T.dim, textAlign: 'center', lineHeight: 1.8 }}>
                  NO MESSAGES YET<br />START THE CONVERSATION
                </div>
              </div>
            )}
            {messages.map((msg, i) => {
              const isMine   = msg.senderId === myUid;
              const prev     = messages[i - 1];
              const showDate = !prev || (
                msg.createdAt && prev.createdAt &&
                msg.createdAt.toDate?.().toDateString() !== prev.createdAt.toDate?.().toDateString()
              );
              return (
                <div key={msg.id}>
                  {showDate && msg.createdAt && (
                    <div style={{ textAlign: 'center', margin: '12px 0 8px',
                      fontFamily: T.mono, fontSize: 8, color: T.dim }}>
                      ── {msg.createdAt.toDate?.().toLocaleDateString('en-IN', {
                        weekday: 'long', day: 'numeric', month: 'short'
                      })} ──
                    </div>
                  )}
                  <MessageBubble msg={msg} isMine={isMine} />
                </div>
              );
            })}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div style={{
            padding: isMobile ? '10px 12px' : compact ? '8px 10px' : '10px 16px',
            borderTop: `1px solid ${T.border}`,
            background: T.panel, position: 'relative', flexShrink: 0,
            paddingBottom: isMobile ? 'calc(10px + env(safe-area-inset-bottom))' : undefined,
          }}>
            <MentionSuggestions suggestions={suggestions} onSelect={selectMention} />
            <form onSubmit={send} style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <div style={{ flex: 1, position: 'relative' }}>
                <textarea
                  ref={inputRef}
                  value={newMsg}
                  onChange={handleInput}
                  onKeyDown={handleKeyDown}
                  placeholder={`Message ${(activeMember.name || '').split(' ')[0] || 'team'}…`}
                  rows={1}
                  disabled={!cryptoReady}
                  style={{
                    width: '100%', background: T.panel2, border: `1px solid ${T.border}`,
                    borderRadius: isMobile ? 22 : 12, outline: 'none', color: T.bright,
                    fontFamily: T.sans, fontSize: isMobile ? 16 : compact ? 12 : 14,
                    padding: isMobile ? '11px 16px' : compact ? '8px 12px' : '10px 14px',
                    resize: 'none', overflow: 'hidden', boxSizing: 'border-box', lineHeight: 1.5,
                    transition: 'border-color 0.15s',
                    opacity: cryptoReady ? 1 : 0.5,
                  }}
                  onFocus={e => e.target.style.borderColor = T.accent}
                  onBlur={e => e.target.style.borderColor = T.border}
                />
              </div>
              <button
                type="submit"
                disabled={!newMsg.trim() || sending || !cryptoReady}
                style={{
                  width: isMobile ? 46 : compact ? 36 : 44,
                  height: isMobile ? 46 : compact ? 36 : 44,
                  borderRadius: '50%', flexShrink: 0,
                  background: newMsg.trim() && cryptoReady ? T.accent : T.panel2,
                  border: `1px solid ${newMsg.trim() && cryptoReady ? T.accent : T.border}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: newMsg.trim() && cryptoReady ? 'pointer' : 'default',
                  fontSize: isMobile ? 22 : compact ? 16 : 20, transition: 'all 0.15s', lineHeight: 1,
                }}
              >
                {sending ? <span style={{ fontSize: 10, color: T.muted }}>…</span> : '🚀'}
              </button>
            </form>
            {(!compact && !isMobile) && (
              <div style={{ fontFamily: T.mono, fontSize: 7, color: T.dim, marginTop: 4, paddingLeft: 2 }}>
                Enter to send · Shift+Enter new line · @ to mention
                {sharedKeyRef.current ? ' · 🔐 encrypted' : ' · ⚠ unencrypted (recipient has no key)'}
              </div>
            )}
          </div>
        </div>
      ) : !isMobile ? (
        <div style={{
          flex: 1, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: 14, background: T.bg,
        }}>
          <div style={{ fontSize: compact ? 36 : 52, lineHeight: 1 }}>🛸</div>
          <div style={{ fontFamily: T.mono, fontSize: compact ? 9 : 11, color: T.dim,
            textAlign: 'center', lineHeight: 2, letterSpacing: 2 }}>
            SELECT A MEMBER<br />TO START CHATTING
          </div>
        </div>
      ) : null}
    </div>
  );
}
